import {
  GRAPH_SCOPES,
  DIRECT_UPLOAD_LIMIT,
  UPLOAD_CHUNK_SIZE
} from "./constants.js";

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function errorFromResponse(response, body) {
  const message = body?.error?.message || body?.message || response.statusText || `HTTP ${response.status}`;
  const error = new Error(message);
  error.status = response.status;
  error.body = body;
  return error;
}

export class GraphClient {
  constructor(msalInstance) {
    this.msal = msalInstance;
  }

  async token(account, interactive = true) {
    const request = { account, scopes: GRAPH_SCOPES };
    try {
      const result = await this.msal.acquireTokenSilent(request);
      return result.accessToken;
    } catch (error) {
      if (!interactive) throw error;
      const result = await this.msal.acquireTokenPopup({
        ...request,
        prompt: "select_account"
      });
      return result.accessToken;
    }
  }

  async fetch(account, pathOrUrl, options = {}) {
    const {
      raw = false,
      interactive = true,
      retry = true,
      headers = {},
      ...fetchOptions
    } = options;

    const token = await this.token(account, interactive);
    const url = pathOrUrl.startsWith("http") ? pathOrUrl : `${GRAPH_BASE}${pathOrUrl}`;
    const response = await fetch(url, {
      ...fetchOptions,
      headers: {
        Authorization: `Bearer ${token}`,
        ...headers
      }
    });

    if (response.status === 429 && retry) {
      const seconds = Number(response.headers.get("Retry-After") || 2);
      await sleep(Math.max(1, seconds) * 1000);
      return this.fetch(account, pathOrUrl, { ...options, retry: false });
    }

    if (raw) {
      if (!response.ok) {
        let body;
        try { body = await response.json(); } catch { body = await response.text(); }
        throw errorFromResponse(response, body);
      }
      return response;
    }

    if (response.status === 204) return null;
    let body;
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) body = await response.json();
    else body = await response.text();
    if (!response.ok) throw errorFromResponse(response, body);
    return body;
  }

  getProfile(account) {
    return this.fetch(account, "/me?$select=id,displayName,mail,userPrincipalName");
  }

  getDrive(account) {
    return this.fetch(account, "/me/drive?$select=id,driveType,quota");
  }

  getDriveRoot(account) {
    return this.fetch(account, "/me/drive/root?$select=id,name,webUrl,parentReference");
  }

  getItem(account, itemId) {
    return this.fetch(account, `/me/drive/items/${encodeURIComponent(itemId)}?$select=id,name,size,eTag,cTag,lastModifiedDateTime,file,folder,parentReference,webUrl`);
  }

  async getByPath(account, pathSegments) {
    const encoded = pathSegments.map(x => encodeURIComponent(x)).join("/");
    try {
      return await this.fetch(account, `/me/drive/root:/${encoded}?$select=id,name,size,eTag,cTag,lastModifiedDateTime,file,folder,parentReference,webUrl`);
    } catch (error) {
      if (error.status === 404) return null;
      throw error;
    }
  }

  async listChildren(account, parentId) {
    let url = `/me/drive/items/${encodeURIComponent(parentId)}/children?$select=id,name,size,eTag,cTag,lastModifiedDateTime,file,folder,parentReference,webUrl&$top=200`;
    const items = [];
    while (url) {
      const page = await this.fetch(account, url);
      items.push(...(page.value || []));
      url = page["@odata.nextLink"] || null;
    }
    return items;
  }

  async findChild(account, parentId, name) {
    const children = await this.listChildren(account, parentId);
    return children.find(x => x.name === name) || null;
  }

  async createFolder(account, parentId, name) {
    return this.fetch(account, `/me/drive/items/${encodeURIComponent(parentId)}/children`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name,
        folder: {},
        "@microsoft.graph.conflictBehavior": "fail"
      })
    });
  }

  async ensureFolder(account, parentId, name) {
    const found = await this.findChild(account, parentId, name);
    if (found?.folder) return found;
    try {
      return await this.createFolder(account, parentId, name);
    } catch (error) {
      if (error.status === 409) {
        const retry = await this.findChild(account, parentId, name);
        if (retry?.folder) return retry;
      }
      throw error;
    }
  }

  async downloadBlob(account, itemId) {
    const response = await this.fetch(account, `/me/drive/items/${encodeURIComponent(itemId)}/content`, {
      raw: true,
      redirect: "follow"
    });
    return response.blob();
  }

  async downloadText(account, itemId) {
    const blob = await this.downloadBlob(account, itemId);
    return blob.text();
  }

  async uploadNew(account, folderId, file, { filename = file.name, onProgress } = {}) {
    if (file.size <= DIRECT_UPLOAD_LIMIT) {
      const item = await this.fetch(
        account,
        `/me/drive/items/${encodeURIComponent(folderId)}:/${encodeURIComponent(filename)}:/content?@microsoft.graph.conflictBehavior=rename`,
        {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file
        }
      );
      onProgress?.(1);
      return item;
    }
    return this.uploadNewSession(account, folderId, file, { filename, onProgress });
  }

  async uploadNewSession(account, folderId, file, { filename = file.name, onProgress } = {}) {
    const session = await this.fetch(
      account,
      `/me/drive/items/${encodeURIComponent(folderId)}:/${encodeURIComponent(filename)}:/createUploadSession`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          item: {
            name: filename,
            "@microsoft.graph.conflictBehavior": "rename"
          }
        })
      }
    );
    return this.uploadChunks(session.uploadUrl, file, onProgress);
  }

  async replaceItem(account, itemId, file, { onProgress } = {}) {
    if (file.size <= DIRECT_UPLOAD_LIMIT) {
      const item = await this.fetch(account, `/me/drive/items/${encodeURIComponent(itemId)}/content`, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file
      });
      onProgress?.(1);
      return item;
    }

    const session = await this.fetch(account, `/me/drive/items/${encodeURIComponent(itemId)}/createUploadSession`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        item: { "@microsoft.graph.conflictBehavior": "replace" }
      })
    });
    return this.uploadChunks(session.uploadUrl, file, onProgress);
  }

  async uploadChunks(uploadUrl, file, onProgress) {
    let start = 0;
    let finalItem = null;
    while (start < file.size) {
      const endExclusive = Math.min(start + UPLOAD_CHUNK_SIZE, file.size);
      const chunk = file.slice(start, endExclusive);
      const response = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Length": String(chunk.size),
          "Content-Range": `bytes ${start}-${endExclusive - 1}/${file.size}`
        },
        body: chunk
      });

      let body = null;
      const text = await response.text();
      if (text) {
        try { body = JSON.parse(text); } catch { body = text; }
      }
      if (![200, 201, 202].includes(response.status)) throw errorFromResponse(response, body);
      start = endExclusive;
      onProgress?.(start / file.size);
      if (response.status === 200 || response.status === 201) finalItem = body;
    }
    return finalItem;
  }

  moveItem(account, itemId, destinationFolderId) {
    return this.fetch(account, `/me/drive/items/${encodeURIComponent(itemId)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ parentReference: { id: destinationFolderId } })
    });
  }

  renameItem(account, itemId, newName) {
    return this.fetch(account, `/me/drive/items/${encodeURIComponent(itemId)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newName })
    });
  }

  deleteItem(account, itemId) {
    return this.fetch(account, `/me/drive/items/${encodeURIComponent(itemId)}`, { method: "DELETE" });
  }

  async writeJsonFile(account, folderId, filename, value, { itemId = null, eTag = null } = {}) {
    const blob = new Blob([JSON.stringify(value, null, 2)], { type: "application/json" });
    const path = itemId
      ? `/me/drive/items/${encodeURIComponent(itemId)}/content`
      : `/me/drive/items/${encodeURIComponent(folderId)}:/${encodeURIComponent(filename)}:/content`;
    return this.fetch(account, path, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(eTag ? { "If-Match": eTag } : {})
      },
      body: blob
    });
  }
}
